'use client';

import { useExtendedRouter } from '@hooks/next-routing-wrappers';
import { useAppSelector } from '@store/index';
import { getUserHeaderInfo } from '@store/reducers/userSelector';
import { cn } from '@utils/cn';
import { useMissionNavItems } from '@components/features/candidate-prompt-bar/useMissionNavItems';

/**
 * Mobile-only fixed bottom navigation: the mission links (Jobs, Coach, Talents, MyJobs+).
 * Replaces the mission chips and the footer. Hidden on lg+ where the mission links render
 * flat in the header (HeaderMissionLinks + MainMenuLinks). The in-flow spacer div reserves
 * the bar's height so page content never hides under it.
 */
export const BottomNav = () => {
  const { locale } = useExtendedRouter();
  const headerInfo = useAppSelector(getUserHeaderInfo);
  // MyJobs+ is a plain /my-jobs link like the other missions — its former popup entries
  // all live on the my-jobs page top bar now.
  const { items, handleClick } = useMissionNavItems(locale, Boolean(headerInfo), true);

  const renderLink = ({ item, label }: (typeof items)[number]) => (
    <a
      key={item.key}
      href={item.href}
      aria-current={item.selected ? 'page' : undefined}
      onClick={(event) => {
        // Let the browser handle modifier / middle clicks (open in new tab); intercept
        // plain left-clicks for soft-navigation through handleClick.
        if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
        event.preventDefault();
        // Tapping the active mission from a sub-page returns to mission home; isOnSamePage
        // (inside handleClick) still prevents same-page churn.
        handleClick({ ...item, selected: false });
      }}
      className={cn(
        'flex flex-1 items-center justify-center whitespace-nowrap px-1 text-sm transition-colors',
        item.selected ? 'font-semibold text-secondary' : 'cursor-pointer text-ink-medium hover:text-ink',
      )}
    >
      {label}
    </a>
  );

  return (
    <>
      <div aria-hidden className="h-[calc(3.5rem+env(safe-area-inset-bottom))] print:hidden lg:hidden" />
      <nav className="fixed inset-x-0 bottom-0 z-20 border-t border-input bg-surface pb-[env(safe-area-inset-bottom)] print:hidden lg:hidden">
        <div className="flex h-14 items-stretch">
          {items.map((entry) => renderLink(entry))}
        </div>
      </nav>
    </>
  );
};
